/* eslint-disable react/prop-types, no-unused-vars */
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PremiumInlineGate from '../../../components/premium/PremiumInlineGate';
import { styles } from '../FaceReportStyles';

const PremiumActionSection = ({ data, isPaid, hasIntegrated, handlePayment }) => {
    const { t, i18n } = useTranslation();
    const summaryRef = useRef(null);

    if (!hasIntegrated) return null;

    const actionPlan = data.persona?.analysis_report?.action_plan || {};
    const flirtingTip = data.persona?.flirting_tip || '';
    const isEnglish = String(i18n.resolvedLanguage || i18n.language).startsWith('en');
    const localizedStoredText = (value, fallbackKey) => (
        isEnglish && /[가-힣]/.test(String(value || ''))
            ? t(fallbackKey)
            : value || t(fallbackKey)
    );

    const handleExportSummary = async () => {
        try {
            if (!summaryRef.current) return;
            summaryRef.current.style.display = 'block';

            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(summaryRef.current, {
                backgroundColor: '#0f172a',
                scale: 2,
                logging: false,
                width: 400,
                height: 711,
            });

            summaryRef.current.style.display = 'none';

            const link = document.createElement('a');
            link.download = `Start-Us_Summary_${new Date().getTime()}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (err) {
            console.error('Export failed:', err);
            alert(t('face_result.ui.alert_payment_fail'));
        }
    };

    const exportFooter = (
        <div style={{ marginTop: '3.5rem', textAlign: 'center' }}>
            <button
                onClick={handleExportSummary}
                style={{
                    background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
                    color: '#fff',
                    border: 'none',
                    padding: '1.2rem 2.5rem',
                    borderRadius: '100px',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.8rem',
                    boxShadow: '0 10px 20px rgba(59, 130, 246, 0.3)',
                    transition: 'all 0.3s ease',
                }}
                onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 15px 25px rgba(59, 130, 246, 0.4)';
                }}
                onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 20px rgba(59, 130, 246, 0.3)';
                }}
            >
                <span>📸 {t('face_result.detail.premium.save_story')}</span>
            </button>
        </div>
    );

    return (
        <div style={styles.section}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <div>
                    <h2 style={styles.sectionTitle}>{t('face_result.ui.premium_report_title')}</h2>
                    <p style={styles.sectionDesc}>{t('face_result.ui.premium_report_desc')}</p>
                </div>
            </div>

            <div style={styles.premiumCard}>
                <div style={styles.cardHeader}>
                    <div style={styles.iconBadge}>👤</div>
                    <div>
                        <h3 style={styles.cardTitle}>{t('face_result.ui.social_role')}</h3>
                        <p style={styles.cardSubtitle}>{t('face_result.ui.social_role_sub')}</p>
                    </div>
                </div>
                <div>
                    <p style={{
                        ...styles.cardContent,
                        fontSize: '1.25rem',
                        fontWeight: '600',
                        color: '#3b82f6',
                    }}>
                        {localizedStoredText(data.social_role, 'face_result.detail.premium.social_role_fallback')}
                    </p>
                </div>
            </div>

            <div style={{ marginTop: '2rem' }}>
                <PremiumInlineGate
                    locked={!isPaid}
                    title={t('face_result.detail.premium.gate_title')}
                    description={t('face_result.detail.premium.gate_description')}
                    primaryActionLabel={t('face_result.detail.premium.gate_cta')}
                    onPrimaryAction={handlePayment}
                    footer={isPaid ? exportFooter : null}
                >
                    <div style={styles.twoColumnGrid}>
                        <div style={styles.premiumCard}>
                            <div style={styles.cardHeader}>
                                <div style={{ ...styles.iconBadge, background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }}>💡</div>
                                <div>
                                    <h3 style={styles.cardTitle}>{t('face_result.ui.deep_insight')}</h3>
                                    <p style={styles.cardSubtitle}>{t('face_result.ui.deep_insight_sub')}</p>
                                </div>
                            </div>
                            <p style={{ ...styles.cardContent, lineHeight: '1.8' }}>
                                {localizedStoredText(data.deep_insight, 'face_result.detail.premium.deep_insight_fallback')}
                            </p>
                        </div>

                        <div style={styles.premiumCard}>
                            <div style={styles.cardHeader}>
                                <div style={{ ...styles.iconBadge, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>⚠️</div>
                                <div>
                                    <h3 style={styles.cardTitle}>{t('face_result.ui.blind_spots')}</h3>
                                    <p style={styles.cardSubtitle}>{t('face_result.ui.blind_spots_sub')}</p>
                                </div>
                            </div>
                            <p style={{ ...styles.cardContent, lineHeight: '1.8' }}>
                                {localizedStoredText(data.blind_spots, 'face_result.detail.premium.blind_spots_fallback')}
                            </p>
                        </div>
                    </div>

                    <div style={{ ...styles.actionPlanCard, marginBottom: 0 }}>
                        <div style={styles.cardHeader}>
                            <div style={{ ...styles.iconBadge, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>✅</div>
                            <div>
                                <h3 style={styles.cardTitle}>{t('face_result.detail.premium.action_plan')}</h3>
                                <p style={styles.cardSubtitle}>{t('face_result.detail.premium.action_plan_description')}</p>
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem', marginTop: '1.5rem' }}>
                            <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1.5rem', borderRadius: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.8rem' }}>
                                    <span style={{ fontSize: '1.5rem' }}>💇‍♂️</span>
                                    <h4 style={{ margin: 0, color: '#f1f5f9', fontSize: '1.1rem' }}>{t('face_result.detail.premium.hair_strategy')}</h4>
                                </div>
                                <p style={{ margin: 0, color: '#cbd5e1', lineHeight: '1.6' }}>{actionPlan.hair || t('face_result.detail.premium.missing_data')}</p>
                            </div>

                            <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1.5rem', borderRadius: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.8rem' }}>
                                    <span style={{ fontSize: '1.5rem' }}>👔</span>
                                    <h4 style={{ margin: 0, color: '#f1f5f9', fontSize: '1.1rem' }}>{t('face_result.detail.premium.fashion_strategy')}</h4>
                                </div>
                                <p style={{ margin: 0, color: '#cbd5e1', lineHeight: '1.6' }}>{actionPlan.fashion || t('face_result.detail.premium.missing_data')}</p>
                            </div>

                            <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1.5rem', borderRadius: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.8rem' }}>
                                    <span style={{ fontSize: '1.5rem' }}>👜</span>
                                    <h4 style={{ margin: 0, color: '#f1f5f9', fontSize: '1.1rem' }}>{t('face_result.detail.premium.signature_item')}</h4>
                                </div>
                                <p style={{ margin: 0, color: '#cbd5e1', lineHeight: '1.6' }}>{actionPlan.item || t('face_result.detail.premium.missing_data')}</p>
                            </div>

                            <div style={{ background: 'linear-gradient(135deg, rgba(236,72,153,0.1), rgba(139,92,246,0.1))', padding: '1.5rem', borderRadius: '16px', border: '1px solid rgba(236,72,153,0.3)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.8rem' }}>
                                    <span style={{ fontSize: '1.5rem' }}>💘</span>
                                    <h4 style={{ margin: 0, color: '#fce7f3', fontSize: '1.15rem', fontWeight: 'bold' }}>{t('face_result.detail.premium.secret_flirting_tip')}</h4>
                                </div>
                                <p style={{ margin: 0, color: '#fbcfe8', lineHeight: '1.6', fontWeight: '500' }}>{flirtingTip || t('face_result.detail.premium.missing_flirting_tip')}</p>
                            </div>
                        </div>
                    </div>
                </PremiumInlineGate>
            </div>

            <div
                ref={summaryRef}
                style={{
                    display: 'none',
                    width: '400px',
                    height: '711px',
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
                    position: 'absolute',
                    left: '-9999px',
                    top: '-9999px',
                    padding: '2.5rem',
                    boxSizing: 'border-box',
                    borderRadius: '32px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    boxShadow: 'inset 0 0 50px rgba(0,0,0,0.5)',
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                    <div>
                        <div style={{ color: '#94a3b8', fontSize: '1rem', marginBottom: '1.5rem', fontWeight: 'bold', letterSpacing: '0.1em' }}>{t('face_result.detail.premium.ai_analysis')}</div>

                        <div style={{ display: 'inline-block', background: 'rgba(59,130,246,0.2)', color: '#60a5fa', padding: '0.4rem 1rem', borderRadius: '100px', fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                            {data.masculinity_label}
                        </div>

                        <h1 style={{ fontSize: '2.4rem', color: '#fff', margin: '0 0 0.8rem 0', lineHeight: '1.2', fontWeight: '900' }}>
                            {data.persona?.title || t('face_result.detail.premium.unique_appeal')}
                        </h1>
                        <p style={{ color: '#cbd5e1', fontSize: '1.1rem', margin: 0, fontStyle: 'italic', lineHeight: '1.5' }}>
                            &quot;{data.persona?.killer_copy || data.persona?.description || t('face_result.detail.premium.analysis_complete')}&quot;
                        </p>
                    </div>

                    <div style={{ background: 'rgba(255,255,255,0.05)', padding: '1.8rem', borderRadius: '24px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <div style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: 'bold' }}>{t('face_result.detail.premium.persona_type')}</div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <span style={{ background: '#3b82f6', color: '#fff', padding: '0.4rem 1rem', borderRadius: '8px', fontSize: '1rem', fontWeight: 'bold' }}>
                                    {data.persona?.base_type || 'Base'}
                                </span>
                                <span style={{ background: '#f472b6', color: '#fff', padding: '0.4rem 1rem', borderRadius: '8px', fontSize: '1rem', fontWeight: 'bold' }}>
                                    {data.persona?.spice_type || 'Spice'}
                                </span>
                            </div>
                        </div>

                        <div>
                            <div style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: 'bold' }}>{t('face_result.detail.premium.gap')}</div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                                <span style={{ fontSize: '2.5rem', color: '#34d399', fontWeight: '900' }}>
                                    {data.congruence_score?.toFixed(1) || '0.0'}%
                                </span>
                                <span style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>{t('face_result.detail.premium.aligned')}</span>
                            </div>
                        </div>
                    </div>

                    <div style={{ textAlign: 'center', color: '#64748b', fontSize: '0.9rem', fontWeight: 'bold', marginTop: '1rem' }}>
                        {t('face_result.detail.premium.footer')}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PremiumActionSection;
